// Stage 6 of the Smart Asset Import pipeline: deterministic routing.
//
// Strict mode (the only mode in v1): a row is AUTO_ACCEPT only if it carries ZERO blocking
// or review flags AND every business-critical master value resolved EXACT. Anything else is
// REVIEW. A row missing a hard-required field that cannot be derived is BLOCKED.
//
// This is intentionally conservative — the user chose exact-match-only. AUTO_ACCEPT is a
// promise that no human judgement is required for that row; we only make that promise when
// the deterministic evidence is complete.

export const AUTO_ACCEPT_THRESHOLD = 100; // percent; single tunable knob

// Flags that force a human look but do not make the row unusable.
export const REVIEW_FLAGS = new Set([
  'UNMATCHED_MASTER',   // a type/subtype/model/make/location value is not in AssetPro yet
  'NO_MASTER',          // master data could not be loaded, so EXISTS/NEW is unknown
  'AMBIGUOUS_DATE',     // purchase date could be read two ways
  'INVALID_DATE',       // purchase date could not be parsed
  'DUPLICATE',          // identical business key to another row in this file
  'QTY_ASSUMED',        // QTY not supplied; assumed 1 for a single itemized unit
  'ZERO_QTY',           // QTY is a literal 0 — confirm intentional
  'AGGREGATE',          // type + count only; not an itemized asset
  'HEADER_AMBIGUOUS',   // header row detection was not confident
  'SOURCE_COLUMNS_DROPPED', // recognized source columns not carried into the template
  'MISSING_REQUIRED',   // a required template field is empty (user must supply it)
]);

// Flags that make the row unsafe to emit at all without human input.
export const BLOCKING_FLAGS = new Set([
  'ROW_EMPTY',          // no asset type / sub type / model — nothing meaningful to import
]);

/**
 * @param {string[]} flags  flags accumulated for the row
 * @returns {{ status:'AUTO_ACCEPT'|'REVIEW'|'BLOCKED', reviewReasons:string[], blockingReasons:string[] }}
 */
export const routeRow = (flags = []) => {
  const blockingReasons = flags.filter((f) => BLOCKING_FLAGS.has(f));
  const reviewReasons = flags.filter((f) => REVIEW_FLAGS.has(f));

  if (blockingReasons.length > 0) {
    return { status: 'BLOCKED', reviewReasons, blockingReasons };
  }
  if (reviewReasons.length > 0) {
    return { status: 'REVIEW', reviewReasons, blockingReasons };
  }
  return { status: 'AUTO_ACCEPT', reviewReasons, blockingReasons };
};
